import { useEffect } from 'react';

import localStorageUtil from 'src/utils/localStorageUtil';
import { storageConstants } from 'src/utils/constants/storageConstants';

import memberApi from 'src/api/membershipApi';

/**
 * iOS 네이티브 앱에서 FCM 푸시 토큰을 전달받는 전역 함수
 *
 * 중요: 이 함수는 모듈 레벨에서 즉시 등록됩니다.
 * 이유: iOS WKWebView는 React 컴포넌트가 마운트되기 전에도 JavaScript 함수를 호출할 수 있습니다.
 * useEffect 내부에서 등록하면 함수가 아직 정의되지 않아 "undefined is not a function" 오류가 발생합니다.
 *
 * 주의사항:
 * - async/await를 사용하지 않습니다 (iOS WKWebView는 Promise 반환을 지원하지 않음)
 * - 반환 타입은 void입니다 (WKErrorDomain Code=5 오류 방지)
 *
 * @param token - iOS 네이티브 앱에서 전달받은 FCM 푸시 토큰
 */
window.getPushToken = (token: string) => {
  console.log('GET PUSH TOKEN BRIDGE > ', token);

  const handlePushToken = () => {
    // 1단계: localStorage에 FCM 토큰 저장
    localStorage.setItem(storageConstants.fbToken, token);

    /**
     * 2단계: 로그인 상태 확인 및 추가 처리
     * - 이미 로그인된 상태라면 서버에 즉시 전송할 수 있도록 준비
     * - 언어 코드와 함께 푸시 토큰 정보를 로깅
     */
    const accessToken = localStorageUtil.getToken(); // 로그인 판별
    if (!accessToken) return;

    const languageInfo = localStorage.getItem(storageConstants.languageInfo);
    const languageCode = languageInfo ? JSON.parse(languageInfo).code : null;

    if (!languageCode) return;

    const params = {
      option: {
        pushToken: token,
        pushLanguage: languageCode,
      },
    };

    // iOS WKWebView 제약: async/await 금지, Promise 반환 금지
    memberApi
      .callPushNotificationApi(params)
      .then(() => {
        console.log('[PushToken] options API 호출 성공');
      })
      .catch((error) => {
        console.error('[PushToken] options API 호출 실패:', error);
      });
  };

  handlePushToken();
};

/**
 * 리모트 컨피그 실패 시 iOS 네이티브 앱에서 호출하는 전역 함수
 *
 * @param errorMessage - 리모트 컨피그 실패 시 전달받은 에러 메시지
 */
window.onRemoteConfigError = (errorMessage: string) => {
  // 화면 처리를 위한 콘솔 로그 출력
  console.error('[Remote Config Error]', errorMessage);
};

/**
 * 푸시 브릿지 훅
 *
 * 이 훅은 iOS 네이티브 앱과의 통신을 위한 전역 함수를 등록합니다.
 * 함수는 모듈 레벨에서 이미 등록되어 있으므로,
 * 이 훅은 App 컴포넌트에서 사용됩니다.
 *
 * 사용법:
 * ```tsx
 * function App() {
 *   usePushBridge();
 *   return <div>...</div>;
 * }
 * ```
 */
const usePushBridge = () => {
  useEffect(() => {
    // 함수는 모듈 레벨에서 이미 등록되어 있음
    // 필요시 여기에 추가 초기화 로직을 작성할 수 있습니다
    // cleanup 함수
    // 주의: 전역 함수를 삭제하면 iOS에서 함수를 호출할 수 없게 되므로
    // 일반적으로 cleanup에서 삭제하지 않습니다.
    // 단, 특정 상황에서 함수를 제거해야 한다면 아래 주석을 해제하세요.
    // return () => {
    //   delete window.getPushToken;
    //   delete window.onRemoteConfigError;
    // };
  }, []);
};

export default usePushBridge;
